(function (root, factory) {
  var create = factory();
  if (typeof module === 'object' && module.exports) module.exports = {createCartGiftService: create};
  else root.KMCreateCartGiftService = create;
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  return function createCartGiftService(options) {
    var rules = options.rules;
    var routes = options.routes;
    var storage = options.storage;
    var storageKey = 'kmEssentialSelectedGift';
    var selected = null;
    var lastCart = null;
    var lastError = null;
    var queue = Promise.resolve();
    var pendingCount = 0;
    var revision = 0;
    var destroyed = false;
    var changed = false;
    var refreshPromise = null;
    var refreshDirty = false;
    var blockedRestoreKey = null;
    var subscribers = new Set();

    try {
      var stored = JSON.parse(storage && storage.getItem(storageKey));
      if (stored && stored.variantId) selected = String(stored.variantId);
    } catch (error) {}

    function ensureAlive() {
      if (destroyed) throw new Error('Cart gift service destroyed');
    }

    function notify() {
      if (destroyed) return;
      var state = {cart: lastCart, selectedVariantId: selected, pending: pendingCount > 0,
        error: lastError, cartChanged: changed};
      subscribers.forEach(function (listener) { listener(state); });
      if (!pendingCount) changed = false;
    }

    function saveSelection(id, config) {
      ensureAlive();
      selected = id == null ? null : String(id);
      try {
        if (selected) {
          var match = rules.findGiftVariant(config, selected);
          storage && storage.setItem(storageKey, JSON.stringify({variantId: selected,
            handle: match && match.gift.handle, giftId: match && match.gift.id}));
        } else if (storage) storage.removeItem(storageKey);
      } catch (error) {}
    }

    async function request(url, body) {
      ensureAlive();
      var init = {credentials: 'same-origin', cache: 'no-store',
        headers: {Accept: 'application/json'}};
      if (body) {
        init.method = 'POST';
        init.headers['Content-Type'] = 'application/json';
        init.body = JSON.stringify(body);
      }
      var response = await options.fetchImpl(url, init);
      var payload;
      try { payload = await response.json(); } catch (error) {
        throw new Error('Unable to read cart response');
      }
      ensureAlive();
      if (!response.ok) {
        var requestError = new Error(payload && (payload.description || payload.message) || 'Cart request failed');
        requestError.status = response.status;
        throw requestError;
      }
      if (body) changed = true;
      return payload;
    }

    async function readCart() {
      var cart = await request(routes.cart);
      if (!cart || !Array.isArray(cart.items) || !Number.isFinite(Number(cart.total_price))) {
        throw new Error('Invalid cart response');
      }
      lastCart = cart;
      return cart;
    }

    function enqueue(operation) {
      if (destroyed) return Promise.reject(new Error('Cart gift service destroyed'));
      pendingCount += 1;
      lastError = null;
      notify();
      var result = queue.then(function () { ensureAlive(); return operation(); });
      queue = result.catch(function () {});
      return result.then(function (cart) {
        pendingCount -= 1;
        lastError = null;
        notify();
        return cart;
      }, function (error) {
        pendingCount -= 1;
        lastError = error;
        notify();
        throw error;
      });
    }

    function usable(config, id) {
      var match = config.enabled ? rules.findGiftVariant(config, id) : null;
      return match && match.variant.available ? match : null;
    }

    function hasGift(cart, id) {
      return cart.items.some(function (item) {
        return rules.isModernGift(item) && String(item.variant_id || item.id) === id;
      });
    }

    function restoreKey(cart, config, id) {
      var match = rules.findGiftVariant(config, id);
      return [id, match && match.thresholdCents, rules.getEligibleSubtotal(cart, config),
        cart.items.filter(function (item) { return !rules.isModernGift(item); })
          .map(function (item) { return item.key + ':' + item.quantity; }).join('|')].join(';');
    }

    async function reconcile(cart, config, id) {
      var updates = rules.getGiftUpdates(cart, config, id);
      if (Object.keys(updates).length) {
        await request(routes.update, {updates: updates});
        cart = await readCart();
      }
      var match = usable(config, id);
      if (match && cart.items.length && rules.getEligibleSubtotal(cart, config) >= match.thresholdCents
        && !hasGift(cart, match.variant.id) && blockedRestoreKey !== restoreKey(cart, config, id)) {
        await request(routes.add, {id: match.variant.id, quantity: 1, properties: {_km_cart_gift: 'true'}});
        cart = await readCart();
        var postAddUpdates = rules.getGiftUpdates(cart, config, id);
        if (Object.keys(postAddUpdates).length) {
          await request(routes.update, {updates: postAddUpdates});
          cart = await readCart();
          blockedRestoreKey = restoreKey(cart, config, id);
        } else blockedRestoreKey = null;
      }
      lastCart = cart;
      return cart;
    }

    async function sync() {
      var config = rules.normalizeConfig(options.getConfig());
      var cart = await readCart();
      if (!config.enabled || !cart.items.length) saveSelection(null, config);
      else {
        if (selected && !usable(config, selected)) saveSelection(null, config);
        if (!selected) {
          var existing = cart.items.find(function (item) {
            return rules.isModernGift(item) && usable(config, String(item.variant_id || item.id));
          });
          if (existing) saveSelection(String(existing.variant_id || existing.id), config);
        }
      }
      return reconcile(cart, config, selected);
    }

    function refresh() {
      if (destroyed) return Promise.reject(new Error('Cart gift service destroyed'));
      if (refreshPromise) {
        refreshDirty = true;
        return refreshPromise;
      }
      refreshPromise = enqueue(async function () {
        var cart;
        do {
          refreshDirty = false;
          cart = await sync();
        } while (refreshDirty && !destroyed);
        return cart;
      });
      refreshPromise.then(function () { refreshPromise = null; }, function () { refreshPromise = null; });
      return refreshPromise;
    }

    function chooseGift(variantId) {
      var choiceRevision = ++revision;
      var id = String(variantId);
      return enqueue(async function () {
        var config = rules.normalizeConfig(options.getConfig());
        var match = usable(config, id);
        if (!match) throw new Error(config.messages.unavailable || 'Gift unavailable');
        var cart = await readCart();
        if (choiceRevision !== revision) return cart;
        if (!cart.items.length) {
          saveSelection(null, config);
          throw new Error('Add an item before choosing a gift');
        }
        blockedRestoreKey = null;
        var previous = selected;
        try {
          cart = await reconcile(cart, config, id);
          if (choiceRevision === revision) saveSelection(id, config);
          return cart;
        } catch (error) {
          // Replacement is two API calls; restore an eligible previous gift only once.
          try {
            cart = await readCart();
            var oldMatch = usable(config, previous);
            if (choiceRevision === revision && oldMatch
              && rules.getEligibleSubtotal(cart, config) >= oldMatch.thresholdCents) {
              await reconcile(cart, config, previous);
            }
          } catch (restoreError) {
            try { await readCart(); } catch (readError) {}
          }
          throw error;
        }
      });
    }

    return {
      refresh: refresh,
      chooseGift: chooseGift,
      prepareCheckout: function () { return enqueue(sync); },
      getSelectedVariantId: function () { return selected; },
      subscribe: function (listener) {
        if (destroyed) return function () {};
        subscribers.add(listener);
        listener({cart: lastCart, selectedVariantId: selected, pending: pendingCount > 0,
          error: lastError, cartChanged: false});
        return function () { subscribers.delete(listener); };
      },
      destroy: function () { destroyed = true; subscribers.clear(); }
    };
  };
});
